
import React, { useState } from 'react';
import { Mail, Phone, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload = {
      name: formData.name,
      email: formData.email,
      company: formData.company || null,
      message: formData.message
    };

    let insertFailed = false;
    let emailFailed = false;

    try {
      // Store the enquiry
      const { error } = await supabase.from('contact_submissions').insert(payload);

      if (error) {
        insertFailed = true;
        console.error('Error storing contact submission:', error);
      }

      // Always attempt the notification, even if storage failed,
      // so an enquiry is never lost entirely.
      try {
        const { error: fnError } = await supabase.functions.invoke('send-contact-notification', {
          body: {
            name: formData.name,
            email: formData.email,
            company: formData.company,
            message: formData.message
          }
        });
        if (fnError) throw fnError;
      } catch (emailError) {
        emailFailed = true;
        console.error('Error sending email notification:', emailError);
      }

      if (insertFailed && emailFailed) {
        toast.error(
          `We couldn't send your message. Please email us directly at connect@thinktomorrow.ai. (${'Submission failed'})`
        );
        return;
      }

      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({
        name: '',
        email: '',
        company: '',
        message: ''
      });
    } catch (err) {
      console.error('Unexpected error submitting contact form:', err);
      toast.error(
        `An unexpected error occurred: ${err instanceof Error ? err.message : 'Unknown error'}. Please email connect@thinktomorrow.ai.`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  
  return (
    <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
      {/* Contact Information */}
      <div>
        <h3 className="text-2xl font-bold font-space mb-6">Get in Touch</h3>
        <p className="text-gray-600 mb-8">
          Have questions about how AI can help your business? Want to learn more about our services? 
          We're here to help. Reach out to us using the form or contact details below.
        </p>
        
        <div className="space-y-6">
          <div className="flex items-start">
            <div className="mt-1 mr-4 p-2 bg-brand-teal/10 rounded-full">
              <Mail className="text-brand-teal" size={22} />
            </div>
            <div>
              <h4 className="font-semibold mb-1">Email Us</h4>
              <a href="mailto:connect@thinktomorrow.ai" className="text-brand-teal hover:underline">
                connect@thinktomorrow.ai
              </a>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="mt-1 mr-4 p-2 bg-brand-teal/10 rounded-full">
              <Phone className="text-brand-teal" size={22} />
            </div>
            <div>
              <h4 className="font-semibold mb-1">Call Us</h4>
              <a href="tel:+64212763763" className="text-brand-teal hover:underline">
                021-276-3763
              </a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Contact Form */}
      <div className="glass-card p-8">
        <h3 className="text-2xl font-bold font-space mb-6">Send a Message</h3>
        <form onSubmit={handleSubmit}>
          <div className="space-y-5">
            <div>
              <label htmlFor="name" className="block text-sm font-medium mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-teal"
                placeholder="Enter your name"
              />
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-teal"
                placeholder="Enter your email"
              />
            </div>
            
            <div>
              <label htmlFor="company" className="block text-sm font-medium mb-1">
                Company Name (Optional)
              </label>
              <input
                id="company"
                name="company"
                type="text"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-teal"
                placeholder="Enter your company name"
              />
            </div>
            
            <div>
              <label htmlFor="message" className="block text-sm font-medium mb-1">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                rows={4}
                required
                value={formData.message}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-teal"
                placeholder="Tell us about your business and how we can help"
              />
            </div>
            
            <div>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={isSubmitting}
                icon={isSubmitting ? undefined : <Send size={18} />}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;
